import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

import { EditDialog } from '../../../EditDialog';
import { EditFormField } from '../../../EditFormField';
import {
  type AccountSummaryData,
  GET_ACCOUNT_SUMMARY
} from '../queries/accountSummaryQuery';

// Field mappings for social media
interface FieldMapping {
  apiField: string;
  formField: keyof AccountSummaryData;
}

const SOCIAL_MEDIA_FIELDS: FieldMapping[] = [
  { apiField: 'facebook', formField: 'facebook' },
  { apiField: 'instagram', formField: 'instagram' },
  { apiField: 'linkedin', formField: 'linkedin' },
  { apiField: 'twitter', formField: 'twitter' },
  { apiField: 'youtube', formField: 'youtube' }
];

// Get changed fields between current and initial values
function getChangedValues(
  fields: FieldMapping[],
  current: AccountSummaryData,
  initial: AccountSummaryData
): Record<string, unknown> {
  const changes: Record<string, unknown> = {};

  for (const { apiField, formField } of fields) {
    const currentVal = current[formField];
    const initialVal = initial[formField];

    if (currentVal !== initialVal) {
      changes[apiField] = currentVal === '' ? null : currentVal;
    }
  }

  return changes;
}

// Mutation for upserting social media
const UPSERT_SOCIAL_MEDIA = gql`
  mutation UpsertSocialMedia($accountId: ID!, $values: SocialMediaInput!) {
    upsertSocialMedia(accountId: $accountId, values: $values) {
      ok
      created
      socialMedia {
        id
        accountId {
          id
          name
        }
        linkedin
        facebook
        instagram
        twitter
        youtube
      }
    }
  }
`;

export interface EditSocialMediaDialogProps {
  accountId: string;
  initialValue: AccountSummaryData;
  isOpen: boolean;
  onChange: (data: AccountSummaryData) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  value: AccountSummaryData;
}

const urlSchema = z.string().refine(
  val => {
    if (val === '') return true;
    return z.url().safeParse(val).success;
  },
  {
    message: 'Please enter a valid URL (e.g., https://example.com)'
  }
);

const socialMediaSchema = z.object({
  facebook: urlSchema,
  instagram: urlSchema,
  linkedin: urlSchema,
  twitter: urlSchema,
  youtube: urlSchema
});

export function EditSocialMediaDialog({
  accountId,
  initialValue,
  isOpen,
  onChange,
  onOpenChange,
  onSave,
  value
}: EditSocialMediaDialogProps) {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  const [upsertSocialMedia] = useMutation(UPSERT_SOCIAL_MEDIA, {
    refetchQueries: [
      { query: GET_ACCOUNT_SUMMARY, variables: { id: accountId } }
    ]
  });

  const socialMediaChanges = getChangedValues(
    SOCIAL_MEDIA_FIELDS,
    value,
    initialValue
  );
  const isDirty = Object.keys(socialMediaChanges).length > 0;

  const validateField = (field: string, fieldValue: string) => {
    const result = urlSchema.safeParse(fieldValue);
    if (!result.success && result.error.issues.length > 0) {
      setErrors(prev => ({
        ...prev,
        [field]: result.error.issues[0]?.message ?? 'Invalid URL'
      }));
    } else {
      setErrors(prev => {
        const newErrors = { ...prev };
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const updateField = <K extends keyof AccountSummaryData>(
    field: K,
    fieldValue: AccountSummaryData[K]
  ) => {
    const stringValue = fieldValue as string;
    validateField(field as string, stringValue);
    onChange({ ...value, [field]: fieldValue });
  };

  const handleSave = async () => {
    // Validate all fields first
    const result = socialMediaSchema.safeParse({
      facebook: value.facebook,
      instagram: value.instagram,
      linkedin: value.linkedin,
      twitter: value.twitter,
      youtube: value.youtube
    });

    if (!result.success) {
      const newErrors: Record<string, string> = {};
      result.error.issues.forEach(issue => {
        const path = issue.path[0];
        if (typeof path === 'string') {
          newErrors[path] = issue.message;
        }
      });
      setErrors(newErrors);
      return;
    }

    if (!isDirty) {
      onSave();
      return;
    }

    setIsSaving(true);

    try {
      const response = await upsertSocialMedia({
        variables: {
          accountId,
          values: socialMediaChanges
        }
      });

      const data = response.data as {
        upsertSocialMedia?: { ok?: boolean };
      };

      if (data.upsertSocialMedia?.ok === false) {
        toast.error('Failed to save social media');
      } else {
        setErrors({});
        toast.success('Social media saved successfully');
        onSave();
      }
    } catch (error) {
      console.error('Error saving social media:', error);
      toast.error('Failed to save social media');
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setErrors({});
    }
    onOpenChange(open);
  };

  return (
    <EditDialog
      isDirty={isDirty}
      isOpen={isOpen}
      isSaving={isSaving}
      onOpenChange={handleOpenChange}
      onSave={handleSave}
      title="Social Media"
    >
      <div className="grid grid-cols-2 gap-x-6 gap-y-4">
        <EditFormField
          error={errors.facebook}
          label="Facebook"
          onChange={v => {
            updateField('facebook', v);
          }}
          placeholder="Enter Facebook URL"
          type="url"
          value={value.facebook}
        />
        <EditFormField
          error={errors.instagram}
          label="Instagram"
          onChange={v => {
            updateField('instagram', v);
          }}
          placeholder="Enter Instagram URL"
          type="url"
          value={value.instagram}
        />
        <EditFormField
          error={errors.linkedin}
          label="LinkedIn"
          onChange={v => {
            updateField('linkedin', v);
          }}
          placeholder="Enter LinkedIn URL"
          type="url"
          value={value.linkedin}
        />
        <EditFormField
          error={errors.twitter}
          label="Twitter"
          onChange={v => {
            updateField('twitter', v);
          }}
          placeholder="Enter Twitter URL"
          type="url"
          value={value.twitter}
        />
        <EditFormField
          error={errors.youtube}
          label="YouTube"
          onChange={v => {
            updateField('youtube', v);
          }}
          placeholder="Enter YouTube URL"
          type="url"
          value={value.youtube}
        />
      </div>
    </EditDialog>
  );
}
